import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
  Avatar,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  alpha,
  useTheme,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { stripePromise } from "index";
import { getBankLogoUrl } from "lib/bankLogos";
import { Elements } from "@stripe/react-stripe-js";

import {
  useLazyGetCreateIntentQuery,
  useGetPaymentsQuery,
  useDeleteBankMutation,
} from "components/Slices/accountsApiSlice";
import { useGetProfileQuery } from "components/Slices/usersApiSlice";
import { useSelector } from "react-redux";

import SetupForm from "./components/SetupForm";
import { RootState } from "store";

const maskCardNumber = (last4: string) => `•••• •••• •••• ${last4}`;
const maskAccountNumber = (n: string | null) => (n ? `••••••${n}` : "");

export function FinancesForm() {
  const theme = useTheme();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openCardModal, setOpenCardModal] = useState(false);

  const user = useSelector((state: RootState) => state.auth.user);
  const { data: profile } = useGetProfileQuery();
  const [deleteBank] = useDeleteBankMutation();
  const { data } = useGetPaymentsQuery();

  const hasCountry = !!profile?.country;
  const [triggerCreateIntent, { data: intentData, isLoading: isCreatingIntent }] = useLazyGetCreateIntentQuery();
  const intentFetched = useRef(false);

  useEffect(() => {
    if (hasCountry && !intentFetched.current) {
      intentFetched.current = true;
      triggerCreateIntent();
    }
  }, [hasCountry, triggerCreateIntent]);

  const sectionCard = {
    borderRadius: 4,
    bgcolor: "background.paper",
    boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
    overflow: "hidden",
  };

  if (isCreatingIntent && hasCountry) {
    return (
      <Box sx={{ ...sectionCard, p: 5, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const client_secret = intentData?.client_secret;
  const userCountry = profile?.country || "CA";
  const isCanadian = userCountry === "CA";
  const creditCards = data?.card;
  const bankAccounts = data?.acss_debit;

  const deleteCard = async (id: string) => {
    try {
      await deleteBank({ bankId: id }).unwrap();
      setSnackbarMessage("Payment method removed successfully");
      setOpenSnackbar(true);
    } catch (e) {
      console.log(e);
    }
  };

  const options = {
    clientSecret: client_secret,
    loader: "always" as const,
    currency: isCanadian ? "cad" : "usd",
  };

  return (
    <Box sx={sectionCard}>
      {/* Header */}
      <Box
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCardIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>
              Payment Methods
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              Manage how you pay into your circles.
            </Typography>
          </Box>
        </Box>

        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={() => setOpenCardModal(true)}
          sx={{ fontSize: "0.78rem", fontWeight: 600, borderRadius: "8px" }}
        >
          Add Method
        </Button>
      </Box>

      <Box sx={{ px: 3.5, py: 3 }}>
        {/* Credit Cards */}
        <Box sx={{ mb: isCanadian ? 3 : 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CreditCardIcon sx={{ fontSize: "0.95rem", color: "text.secondary" }} />
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary" }}>
              Credit Cards
            </Typography>
          </Box>

          {!creditCards ? (
            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", textAlign: "center", py: 3 }}>
              No credit cards added yet.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {creditCards.map((card) => (
                <Grid item xs={12} sm={6} key={card.fingerprint}>
                  <Card
                    variant="outlined"
                    sx={{ borderRadius: 3, height: "100%", border: "1px solid", borderColor: "divider" }}
                  >
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                        {card.display_brand ? (
                          <Avatar
                            src={getBankLogoUrl(card.display_brand)}
                            sx={{ width: 32, height: 32, borderRadius: 1 }}
                          />
                        ) : (
                          <Chip label={card.display_brand} size="small" sx={{ fontSize: "0.72rem" }} />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => deleteCard(card.id)}
                          sx={{ color: "error.main", opacity: 0.5, "&:hover": { opacity: 1, bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                        >
                          <DeleteIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Box>
                      <Typography sx={{ fontFamily: "monospace", fontSize: "0.95rem", letterSpacing: 1, mb: 1 }}>
                        {maskCardNumber(card.last4)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        Expires {card.exp_month}/{card.exp_year}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Bank Accounts (CA only) */}
        {isCanadian && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: "0.95rem", color: "text.secondary" }} />
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary" }}>
                  Bank Accounts
                </Typography>
              </Box>

              {!bankAccounts ? (
                <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", textAlign: "center", py: 3 }}>
                  No bank accounts added yet.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {bankAccounts.map((account) => (
                    <Grid item xs={12} sm={6} key={account.id}>
                      <Card
                        variant="outlined"
                        sx={{ borderRadius: 3, height: "100%", border: "1px solid", borderColor: "divider" }}
                      >
                        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Avatar
                                src={getBankLogoUrl(account.bank_name ?? "") || undefined}
                                sx={{ width: 32, height: 32, borderRadius: 1 }}
                              >
                                <AccountBalanceIcon sx={{ fontSize: "1rem" }} />
                              </Avatar>
                              <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary" }}>
                                {account.bank_name}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => deleteBank({ bankId: account.id })}
                              sx={{ color: "error.main", opacity: 0.5, "&:hover": { opacity: 1, bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                            >
                              <DeleteIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Box>
                          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}>
                            {user?.firstName} {user?.lastName}
                          </Typography>
                          <Typography sx={{ fontFamily: "monospace", fontSize: "0.95rem", letterSpacing: 1, mb: 0.5 }}>
                            {maskAccountNumber(account.institution_number)}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                            Routing: •••••{account.transit_number}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </>
        )}
      </Box>

      {!hasCountry && (
        <Box sx={{ px: 3.5, pb: 3 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Complete your <a href="/onboarding?step=1" style={{ fontWeight: 600, color: "inherit" }}>contact information</a> before adding payment methods.
          </Alert>
        </Box>
      )}

      {client_secret ? (
        <Elements stripe={stripePromise} options={options}>
          <SetupForm open={openCardModal} handleClose={() => setOpenCardModal(false)} />
        </Elements>
      ) : null}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setOpenSnackbar(false)} sx={{ fontSize: "0.82rem" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
